// @flow strict
import React, { useState } from 'react';
import moment from 'moment';
import { Link } from 'gatsby';
import classNames from 'classnames';
import SiteSidebar from '../SiteSidebar';
import styles from './ConstellationV2.module.scss';
import {
  NODES, LINK_STROKE, STAR_PATH, NEIGHBOURS,
  PORTRAIT, PORTRAIT_LINKS, PORTRAIT_DESC_ABOVE, PORTRAIT_VIEWBOX,
  LANDSCAPE, LANDSCAPE_LINKS, LANDSCAPE_VIEWBOX, LANDSCAPE_STARDUST
} from '../Constellation/constellation-data';

type Post = {
  slug: string,
  title: string,
  date: string,
  category: string,
  description: string,
  timeToRead: number
};

type Props = {
  featured: Post[]
};

const ConstellationV2 = ({ featured }: Props) => {
  const [hover, setHover] = useState(null);

  const stateFor = (node) => {
    const isOn = hover === node.key;
    const isNear = !!hover && !isOn && (NEIGHBOURS[node.key] || []).indexOf(hover) !== -1;
    const resting = node.tier === 3 ? 0.92 : 1;

    let star = 'var(--cn-node)';
    if (isOn) star = 'var(--cn-accent)';
    else if (isNear) star = 'var(--cn-node-near)';

    let label = `var(--cn-tier${node.tier})`;
    if (isOn) label = 'var(--cn-label-on)';
    else if (isNear) label = 'var(--cn-node-near)';

    return {
      isOn,
      scale: isOn ? 1.45 : (isNear ? 1.16 : resting),
      groupOpacity: !hover || isOn || isNear ? 1 : 0.87,
      star,
      label
    };
  };

  const pointerIsCoarse = () => window.matchMedia('(hover: none)').matches;

  // Touch browsers synthesise mouseenter and focus from a tap before the
  // click, which would let the first tap navigate straight through.
  const onPoint = (key) => {
    if (pointerIsCoarse()) return;
    setHover(key);
  };

  const onNodeClick = (event, key) => {
    if (pointerIsCoarse() && hover !== key) {
      event.preventDefault();
      setHover(key);
    }
  };

  const strokeFor = (link) => {
    if (hover && (link.from === hover || link.to === hover)) return 'var(--cn-accent)';
    return `var(--cn-link-${link.weight})`;
  };

  // Both layouts are rendered and CSS shows one, because a viewBox and a path
  // cannot be swapped by a media query the way a custom property can.
  const renderLinks = (links, viewBox, className) => (
    <svg
      className={classNames(styles['v2__links'], className)}
      viewBox={`0 0 ${viewBox.w} ${viewBox.h}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <g className={styles['v2__links-group']}>
        {links.map((link) => (
          <path
            key={link.key}
            d={link.d}
            strokeWidth={LINK_STROKE[link.weight]}
            style={{ stroke: strokeFor(link) }}
          />
        ))}
      </g>
    </svg>
  );

  return (
    <div className={styles['v2']}>
      <SiteSidebar className={styles['v2__sidebar']} splitOnMobile />

      <main className={styles['v2__main']}>
        <div className={styles['v2__stage']}>
          {/* The wrapper carries the width cap in single-column mode, because
              the field's percentage padding resolves against its parent's
              width and would otherwise ignore a max-width on the field. */}
          <div className={styles['v2__field-wrap']}>
            <div className={styles['v2__field']}>
              {renderLinks(PORTRAIT_LINKS, PORTRAIT_VIEWBOX, styles['v2__links--portrait'])}
              {renderLinks(LANDSCAPE_LINKS, LANDSCAPE_VIEWBOX, styles['v2__links--landscape'])}

              {/* Ambient stars, behind everything and inert — they carry no
                  link, so they stay out of the tab order and the pointer. */}
              {LANDSCAPE_STARDUST.map((star) => (
                <svg
                  key={star.key}
                  className={styles['v2__dust']}
                  viewBox="0 0 24 24"
                  style={{
                    left: `${star.x}%`,
                    top: `${star.y}%`,
                    // Size travels as a custom property rather than a width, so
                    // the large-screen scale can multiply it — an inline width
                    // would outrank the stylesheet.
                    '--dust-size': `${star.size}px`,
                    animationDuration: star.duration,
                    animationDelay: star.delay,
                    '--dust-lo': star.lo,
                    '--dust-hi': star.hi
                  }}
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d={STAR_PATH} />
                </svg>
              ))}

              {NODES.map((node) => {
                const view = stateFor(node);
                const portrait = PORTRAIT[node.key];
                const landscape = LANDSCAPE[node.key];

                return (
                  <div
                    key={node.key}
                    className={styles['v2__drift']}
                    // Both layouts' coordinates travel as custom properties so
                    // a media query can pick between them; an inline `left`
                    // could not be overridden.
                    style={{
                      '--px': `${portrait.x}%`,
                      '--py': `${portrait.y}%`,
                      '--lx': `${landscape.x}%`,
                      '--ly': `${landscape.y}%`,
                      animationName: node.drift,
                      animationDuration: node.duration,
                      animationDelay: node.delay
                    }}
                  >
                    <Link
                      to={node.to}
                      className={classNames(
                        styles['v2__node'],
                        styles[`v2__node--tier${node.tier}`]
                      )}
                      style={{ opacity: view.groupOpacity }}
                      onMouseEnter={() => onPoint(node.key)}
                      onMouseLeave={() => onPoint(null)}
                      onFocus={() => onPoint(node.key)}
                      onBlur={() => onPoint(null)}
                      onClick={(event) => onNodeClick(event, node.key)}
                    >
                      <svg
                        className={styles['v2__star']}
                        viewBox="0 0 24 24"
                        style={{ transform: `scale(${view.scale})` }}
                        aria-hidden="true"
                        focusable="false"
                      >
                        <path d={STAR_PATH} style={{ fill: view.star }} />
                      </svg>

                      <span className={styles['v2__label']} style={{ color: view.label }}>
                        {node.label}
                      </span>

                      <span
                        className={classNames(styles['v2__desc'], {
                          [styles['v2__desc--above-portrait']]:
                            PORTRAIT_DESC_ABOVE.indexOf(node.key) !== -1
                        })}
                        style={{
                          opacity: view.isOn ? 1 : 0,
                          transform: `translateY(${view.isOn ? 0 : 6}px)`
                        }}
                      >
                        {node.desc}
                      </span>
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <section className={styles['v2__featured']}>
          <header className={styles['v2__featured-head']}>
            <h2 className={styles['v2__featured-title']}>Pieces from the constellation</h2>
            <Link className={styles['v2__featured-all']} to="/articles">All articles</Link>
          </header>

          <div className={styles['v2__cards']}>
            {featured.map((post) => (
              <Link key={post.slug} to={post.slug} className={styles['v2__card']}>
                <span className={styles['v2__card-category']}>{post.category}</span>
                <h3 className={styles['v2__card-title']}>{post.title}</h3>
                <p className={styles['v2__card-description']}>{post.description}</p>
                <span className={styles['v2__card-meta']}>
                  {moment(post.date).format('MMMM YYYY')}
                  {post.timeToRead ? ` · ${post.timeToRead} min` : ''}
                </span>
              </Link>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default ConstellationV2;
